// The Strategy tab reading its own documents: Claude reads one document of the library (a file, or a web
// link's page) and proposes what it adds to the strategy. Nothing joins the content until people say so.
//   reading   one document read once (functions/src/strategy/readings.ts): queued, reading, ready or failed.
//             When ready, what Claude proposes (Reading): elements, each with the document's words it comes
//             from, and links between them and the elements already in the strategy
//   inbox     what people decided about each proposal: accepted, rejected, edited. In the changes' section
//             "inbox" (domain/strategy.ts), key by key, so two reviewers deciding different proposals never
//             overwrite each other:   <reading id>         the document's institution and source, as changed
//                                     <reading id>:<ref>   one proposal's decision and edits
//   add       the accepted ones join the strategy as a new content version (applyReading): elements as the
//             content holds them, their links as proposals the views review as they do every Claude link.
//             A reading can be added to more than once: what it added stays added (ReadingInfo.added).
import { institutionOfSource, libraryEntries, libraryInstitutions, sourcesOf, type LibraryLabels, type LibraryLink } from './library.ts';
import type { StrategyChange, StrategyState } from './strategy.ts';

type Obj = Record<string, unknown>;
const isObj = (v: unknown): v is Obj => !!v && typeof v === 'object' && !Array.isArray(v);
const list = (v: unknown): Obj[] => (Array.isArray(v) ? v.filter(isObj) : []);
const s = (v: unknown): string => (v == null ? '' : String(v));

/** The kinds of element a document holds, as the content names them. */
export const READING_KINDS = ['direction', 'goal', 'programme', 'measure', 'task', 'kpi', 'obligation', 'funding', 'opportunity', 'project'] as const;
export type ReadingKind = (typeof READING_KINDS)[number];

/** The kinds of link, as the views name them: A supports B, A requires B (an obligation on B), A enables B, A depends on B, A conflicts with B. */
export const LINK_TYPES = ['supports', 'requires', 'enables', 'depends_on', 'conflicts'] as const;
export type LinkType = (typeof LINK_TYPES)[number];

export const SOURCE_LEVELS = ['institution', 'unit', 'national', 'EU'] as const;
export type SourceLevel = (typeof SOURCE_LEVELS)[number];

/** The institutions the views know without being told (viewer.html INST0); any other is added as a custom one. */
export const VIEW_INSTITUTIONS = ['VPC', 'LMC', 'VULSK', 'VU MF', 'CDM', 'NAT', 'EU'];

/** One element Claude found. `ref`: its name in this reading (E1, E2…). Texts in Lithuanian, `…En` in English. */
export interface ProposedElement {
  ref: string;
  kind: ReadingKind;
  /** The document's own numbering (I.2, 2.1.3, T14); '' when it has none. */
  code: string;
  /** What it belongs to: a ref of this reading, or an element of the strategy; '' none. */
  parent: string;
  title: string;
  titleEn: string;
  owner: string;
  deadline: string;
  kpi: string;
  kpiEn: string;
  target: string;
  targetEn: string;
  /** A project's actions. */
  actions: string[];
  actionsEn: string[];
  /** The document's own words it was read from, and where: "p. 12", "slide 11", "Strategija!B12". */
  quote: string;
  loc: string;
  /** The element of the strategy it already is: then its links go to that one, and nothing new is added. */
  sameAs: string;
}

/** One link Claude proposes. `from` and `to`: refs of this reading, or elements of the strategy. */
export interface ProposedLink {
  ref: string;
  from: string;
  to: string;
  type: LinkType;
  /** 1 weak … 3 strong */
  strength: number;
  why: string;
  whyEn: string;
  quote: string;
}

/** The document as a source of the strategy, and the institution it belongs to. */
export interface ProposedSource {
  /** The content's source it is (sources[].id), or a new one. */
  id: string;
  title: string;
  titleEn: string;
  level: SourceLevel;
  /** Its own date: 2026, 2026-10, 2026-10-15; '' unknown. */
  date: string;
  /** The institution: one the strategy has, or a new one. */
  inst: string;
  instName: string;
  instNameEn: string;
}

/** What Claude read from one document. */
export interface Reading {
  summary: string;
  summaryEn: string;
  source: ProposedSource;
  elements: ProposedElement[];
  links: ProposedLink[];
}

export type ReadingStatus = 'queued' | 'reading' | 'ready' | 'failed';

/** Waiting for Claude or being read: the document is in hand already. */
export const waitsForClaude = (status: string): boolean => status === 'queued' || status === 'reading';

/** One reading as the page sees it. */
export interface ReadingInfo {
  id: string;
  /** The library's key of the document: f:<file id> or l:<link id>. */
  doc: string;
  name: string;
  status: ReadingStatus;
  by: string;
  at: string;
  /** Being read since (its claim). */
  started?: string;
  ended?: string;
  /** Why it failed; for one waiting again, why the last try did not end. */
  error?: string;
  /** The content version it was read against; null: there was none. */
  version: string | null;
  reading?: Reading;
  /** Each proposal added to the strategy (its ref) and the id it got there. */
  added: Record<string, string>;
  /** The content versions it made. */
  versions: string[];
}

/** GET /api/strategy/readings */
export interface ReadingsBundle {
  readings: ReadingInfo[];
}

/** POST /api/strategy/readings/{id}/add */
export interface ReadingAdded {
  version: { id: string; name: string };
  elements: number;
  links: number;
  same: number;
}

// ── what can be read ────────────────────────────────────────────────────────

/** The files a reading takes, by extension (functions/src/strategy/documents.ts reads them). */
export const READABLE_FILES = ['pdf', 'docx', 'pptx', 'xlsx', 'txt', 'md', 'csv', 'html', 'htm'] as const;
/** A PDF goes to Claude as itself, its pages read as pictures: a larger one is more than one reading holds. */
export const READING_PDF_MAX_MB = 20;
/** Word, PowerPoint, Excel and text: their text is what goes. */
export const READING_FILE_MAX_MB = 50;

const OLD_FORMATS: Record<string, string> = { doc: 'docx', xls: 'xlsx', ppt: 'pptx', rtf: 'docx', odt: 'docx', ods: 'xlsx', odp: 'pptx' };

/** Why Claude cannot read a library file of this name and size, or ''. */
export function readingFileProblem(name: string, size: number): string {
  const ext = /\.([a-z0-9]+)$/i.exec(name)?.[1]?.toLowerCase() ?? '';
  if (OLD_FORMATS[ext]) return `Claude reads .${OLD_FORMATS[ext]}, not .${ext}: save it as .${OLD_FORMATS[ext]} or PDF, and add that.`;
  if (!(READABLE_FILES as readonly string[]).includes(ext)) return 'Claude reads PDF, Word (.docx), PowerPoint (.pptx), Excel (.xlsx) and text files. Save this one as PDF.';
  const max = ext === 'pdf' ? READING_PDF_MAX_MB : READING_FILE_MAX_MB;
  if (size > max * 1024 * 1024) return `This file is over ${max} MB, more than one reading takes: split it${ext === 'pdf' ? ', or make the PDF smaller' : ''}.`;
  return '';
}

// ── checking what Claude answered ───────────────────────────────────────────

export const READING_ELEMENTS_MAX = 400;
export const READING_LINKS_MAX = 800;
const TEXT_MAX = 600;
const QUOTE_MAX = 600;
const SHORT_MAX = 120;
const ACTIONS_MAX = 20;
const DATE = /^\d{4}(-(0[1-9]|1[0-2])(-(0[1-9]|[12]\d|3[01]))?)?$/;
/** A source's or an institution's id: as the content's ("VU MF", "LMC-LIS"). */
export const SOURCE_ID = /^[A-Za-z0-9][A-Za-z0-9 ._-]{0,39}$/;
/** An institution the views add: as their own form asks (viewer.html saveInst). */
const NEW_INST_ID = /^[A-Z0-9][A-Z0-9-]{0,11}$/;
const REF = /^[EL]\d{1,4}$/;

// The answer's fields (claude.ts READING_SCHEMA), and those that are not text.
const SOURCE_FIELDS = ['id', 'title', 'titleEn', 'level', 'date', 'inst', 'instName', 'instNameEn'];
const ELEMENT_FIELDS = ['ref', 'kind', 'code', 'parent', 'title', 'titleEn', 'owner', 'deadline', 'kpi', 'kpiEn', 'target', 'targetEn', 'actions', 'actionsEn', 'quote', 'loc', 'sameAs'];
const LINK_FIELDS = ['ref', 'from', 'to', 'type', 'strength', 'why', 'whyEn', 'quote'];
const NOT_TEXT = ['level', 'kind', 'type', 'strength'];
const textFields = (fields: string[]) => fields.filter((k) => !NOT_TEXT.includes(k));

const text = (v: unknown, max = TEXT_MAX): string => s(v).replace(/\s+/g, ' ').trim().slice(0, max);
const texts = (v: unknown): string[] => (Array.isArray(v) ? v.map((x) => text(x)).filter(Boolean).slice(0, ACTIONS_MAX) : []);
const oneOf = <T extends string>(v: unknown, all: readonly T[], or: T): T => ((all as readonly unknown[]).includes(v) ? (v as T) : or);
/** A code as an id may hold it: letters, digits, dots and dashes. */
const cleanCode = (v: unknown): string =>
  text(v, 40)
    .replace(/[^A-Za-z0-9.-]+/g, '')
    .replace(/^[.-]+|[.-]+$/g, '');

/**
 * What Claude answered, made safe to store and show: texts trimmed and cut to length, unknown kinds and
 * levels replaced, refs renamed E1… and L1… in order (a duplicate is dropped), links whose ends name nothing
 * dropped. `problems`: what was dropped, for the reading's report.
 */
export function readingOf(raw: unknown): { reading: Reading; problems: string[] } {
  const problems: string[] = [];
  const o = isObj(raw) ? raw : {};
  const src = isObj(o.source) ? o.source : {};
  const id = text(src.id, 40);
  const inst = text(src.inst, 40);
  const source: ProposedSource = {
    id: SOURCE_ID.test(id) ? id : '',
    title: text(src.title, 300),
    titleEn: text(src.titleEn, 300),
    level: oneOf(src.level, SOURCE_LEVELS, 'institution'),
    date: DATE.test(text(src.date, 10)) ? text(src.date, 10) : '',
    inst: SOURCE_ID.test(inst) ? inst : '',
    instName: text(src.instName, 120),
    instNameEn: text(src.instNameEn, 120),
  };
  // Claude's refs, renamed in order: the page and the decisions use these. A problem names Claude's own.
  const rename = new Map<string, string>();
  const claudes = (ref: string) => [...rename].find(([, r]) => r === ref)?.[0] ?? ref;
  const elements: ProposedElement[] = [];
  for (const e of list(o.elements)) {
    const was = text(e.ref, 40);
    if (!was || rename.has(was)) {
      problems.push(`An element without a ref of its own was left out (${was || 'none'}).`);
      continue;
    }
    if (elements.length >= READING_ELEMENTS_MAX) {
      problems.push(`Over ${READING_ELEMENTS_MAX} elements: the rest were left out.`);
      break;
    }
    const title = text(e.title);
    if (!title && !text(e.titleEn)) {
      problems.push(`An element without a title was left out (${was}).`);
      continue;
    }
    const ref = `E${elements.length + 1}`;
    rename.set(was, ref);
    elements.push({
      ref,
      kind: oneOf(e.kind, READING_KINDS, 'measure'),
      code: cleanCode(e.code),
      parent: text(e.parent, 80),
      title: title || text(e.titleEn),
      titleEn: text(e.titleEn) || title,
      owner: text(e.owner, 300),
      deadline: text(e.deadline, SHORT_MAX),
      kpi: text(e.kpi),
      kpiEn: text(e.kpiEn),
      target: text(e.target),
      targetEn: text(e.targetEn),
      actions: texts(e.actions),
      actionsEn: texts(e.actionsEn),
      quote: text(e.quote, QUOTE_MAX),
      loc: text(e.loc, SHORT_MAX),
      sameAs: text(e.sameAs, 80),
    });
  }
  // A parent or an end: a ref of this reading (renamed), else an id of the strategy, as given. A ref no element
  // of this reading has (one left out) names nothing: kept, it would name another element once the rest are renamed.
  const end = (v: unknown) => {
    const t = text(v, 80);
    return rename.get(t) ?? (REF.test(t) ? '' : t);
  };
  for (const e of elements) {
    const parent = e.parent;
    e.parent = parent ? end(parent) : '';
    if (parent && !e.parent) problems.push(`${claudes(e.ref)}’s parent ${parent} is no element of this reading: left empty.`);
  }
  const links: ProposedLink[] = [];
  const seen = new Set<string>();
  for (const l of list(o.links)) {
    if (links.length >= READING_LINKS_MAX) {
      problems.push(`Over ${READING_LINKS_MAX} links: the rest were left out.`);
      break;
    }
    const from = end(l.from);
    const to = end(l.to);
    const type = oneOf(l.type, LINK_TYPES, 'supports');
    if (!from || !to || from === to) {
      problems.push(`A link without two ends was left out (${text(l.from, 40)} → ${text(l.to, 40)}).`);
      continue;
    }
    const pair = `${from} ${type} ${to}`;
    if (seen.has(pair)) continue;
    seen.add(pair);
    const strength = Math.round(Number(l.strength));
    links.push({
      ref: `L${links.length + 1}`,
      from,
      to,
      type,
      strength: strength >= 1 && strength <= 3 ? strength : 2,
      why: text(l.why),
      whyEn: text(l.whyEn),
      quote: text(l.quote, QUOTE_MAX),
    });
  }
  return { reading: { summary: text(o.summary, 1000), summaryEn: text(o.summaryEn, 1000), source, elements, links }, problems };
}

/**
 * What in an answer readingOf would change without a word: a list or the source missing (an answer wrapped in
 * another object), a kind, type or level outside its list, a strength other than 1, 2 or 3, a date or an id it
 * would empty or cut. The answer's shape is claude.ts READING_SCHEMA; Claude Code fixes these before its answer
 * is stored.
 */
export function readingShapeProblems(raw: unknown): string[] {
  if (!isObj(raw)) return ['The answer is not a JSON object.'];
  const p: string[] = [];
  const quoted = (v: unknown) => JSON.stringify(v ?? null).slice(0, 60);
  // A field present as something other than text (a list of texts for actions): readingOf would turn it into text.
  const texts = (o: Obj, name: string, keys: readonly string[]) => {
    for (const k of keys) {
      const v = o[k];
      if (v === undefined || v === null) continue;
      const ok = k === 'actions' || k === 'actionsEn' ? Array.isArray(v) && v.every((x) => typeof x === 'string') : typeof v === 'string';
      if (!ok) p.push(`${name}: ${k} ${quoted(v)} is not ${k.startsWith('actions') ? 'a list of texts' : 'text'}.`);
    }
  };
  // A text over what readingOf keeps (cut without a word otherwise), and more actions than it keeps.
  const long = (o: Obj, name: string, limits: Record<string, number>) => {
    for (const [k, max] of Object.entries(limits)) if (typeof o[k] === 'string' && text(o[k], Infinity).length > max) p.push(`${name}: ${k} is over ${max} characters: shorten it.`);
    for (const k of ['actions', 'actionsEn']) {
      const a = o[k];
      if (!Array.isArray(a)) continue;
      // As readingOf counts them: the ones with text.
      if (a.filter((x) => text(x)).length > ACTIONS_MAX) p.push(`${name}: over ${ACTIONS_MAX} ${k}: keep the main ones.`);
      if (a.some((x) => typeof x === 'string' && text(x, Infinity).length > TEXT_MAX)) p.push(`${name}: one of its ${k} is over ${TEXT_MAX} characters: shorten it.`);
    }
  };
  // A field the answer's shape has no place for: misnamed, its value would be lost without a word.
  const unknown = (o: Obj, name: string, keys: readonly string[]) => {
    for (const k of Object.keys(o)) if (!keys.includes(k)) p.push(`${name}: ${quoted(k)} is no field of the answer's shape (schema.json).`);
  };
  unknown(raw, 'the answer', ['summary', 'summaryEn', 'source', 'elements', 'links']);
  for (const k of ['summary', 'summaryEn']) if (raw[k] != null && typeof raw[k] !== 'string') p.push(`${k} is not text.`);
  long(raw, 'the answer', { summary: 1000, summaryEn: 1000 });
  const src = raw.source;
  if (!isObj(src)) p.push('source is missing.');
  else {
    texts(src, 'source', textFields(SOURCE_FIELDS));
    unknown(src, 'source', SOURCE_FIELDS);
    long(src, 'source', { title: 300, titleEn: 300, instName: 120, instNameEn: 120 });
    if (!(SOURCE_LEVELS as readonly unknown[]).includes(src.level)) p.push(`source.level ${quoted(src.level)} is none of ${SOURCE_LEVELS.join(', ')}.`);
    if (text(src.date) && !DATE.test(text(src.date))) p.push(`source.date ${quoted(src.date)} is not 2026, 2026-10 or 2026-10-15.`);
    for (const k of ['id', 'inst'] as const) if (text(src[k]) && !SOURCE_ID.test(text(src[k]))) p.push(`source.${k} ${quoted(src[k])} is not an id (letters, digits, spaces, . _ -; at most 40).`);
  }
  if (!Array.isArray(raw.elements)) p.push('elements is not a list.');
  else
    raw.elements.forEach((e: unknown, i: number) => {
      const o = isObj(e) ? e : {};
      const name = text(o.ref, 40) || `elements[${i}]`;
      if (!isObj(e)) p.push(`${name} is not an object.`);
      if (!(READING_KINDS as readonly unknown[]).includes(o.kind)) p.push(`${name}: kind ${quoted(o.kind)} is none of ${READING_KINDS.join(', ')}.`);
      texts(o, name, textFields(ELEMENT_FIELDS));
      unknown(o, name, ELEMENT_FIELDS);
      long(o, name, { title: TEXT_MAX, titleEn: TEXT_MAX, owner: 300, deadline: SHORT_MAX, kpi: TEXT_MAX, kpiEn: TEXT_MAX, target: TEXT_MAX, targetEn: TEXT_MAX, quote: QUOTE_MAX, loc: SHORT_MAX, parent: 80, sameAs: 80 });
    });
  const pairs = new Map<string, string>();
  if (!Array.isArray(raw.links)) p.push('links is not a list.');
  else
    raw.links.forEach((l: unknown, i: number) => {
      const o = isObj(l) ? l : {};
      const name = `links[${i}] (${text(o.from, 40)} → ${text(o.to, 40)})`;
      if (!(LINK_TYPES as readonly unknown[]).includes(o.type)) p.push(`${name}: type ${quoted(o.type)} is none of ${LINK_TYPES.join(', ')}.`);
      if (![1, 2, 3].includes(o.strength as number)) p.push(`${name}: strength ${quoted(o.strength)} is not 1, 2 or 3.`);
      texts(o, name, textFields(LINK_FIELDS));
      unknown(o, name, LINK_FIELDS);
      long(o, name, { why: TEXT_MAX, whyEn: TEXT_MAX, quote: QUOTE_MAX });
      // The same two ends and kind again: readingOf keeps the first only.
      const pair = `${text(o.from, 80)} ${text(o.type, 40)} ${text(o.to, 80)}`;
      if (pairs.has(pair)) p.push(`${name}: the same link again (${pairs.get(pair)}): merge them.`);
      else pairs.set(pair, `links[${i}]`);
    });
  return p;
}

/** An answer of the reading's shape at all: an object with a source and the two lists. Short of that, nothing is stored. */
export const readingShaped = (raw: unknown): boolean => isObj(raw) && isObj(raw.source) && Array.isArray(raw.elements) && Array.isArray(raw.links);

// ── the inbox: people's decisions ───────────────────────────────────────────

/** A reading's id: its UTC time as a version's (strategy/data.ts versionId), and six hex digits. */
export const READING_ID = /^[0-9TZ-]{10,40}-[0-9a-f]{6}$/;

export type Decision = 'accepted' | 'rejected';

/** The fields of an element a reviewer may change. */
export const ELEMENT_EDITS = ['kind', 'code', 'parent', 'title', 'titleEn', 'owner', 'deadline', 'kpi', 'kpiEn', 'target', 'targetEn', 'sameAs'] as const;
/** The fields of a link a reviewer may change. */
export const LINK_EDITS = ['type', 'strength', 'why', 'whyEn'] as const;
/** The document's institution and source, as a reviewer may change them. */
export const SOURCE_EDITS = ['id', 'title', 'titleEn', 'level', 'date', 'inst', 'instName', 'instNameEn'] as const;

export interface InboxDecision {
  s?: Decision;
  edit?: Partial<Pick<ProposedElement, (typeof ELEMENT_EDITS)[number]>> & Partial<Pick<ProposedLink, (typeof LINK_EDITS)[number]>>;
}

export const inboxKey = (readingId: string, ref = ''): string => (ref ? `${readingId}:${ref}` : readingId);

/** Why a change of the section "inbox" cannot be stored, or ''. */
export function inboxEntryProblem(key: string, value: unknown): string {
  const [id, ref, more] = key.split(':');
  if (!id || !READING_ID.test(id) || more !== undefined || (ref !== undefined && !REF.test(ref))) return 'Unknown proposal.';
  if (value === null) return '';
  if (!isObj(value)) return 'A decision is an object.';
  if (ref === undefined) {
    for (const [k, v] of Object.entries(value)) {
      if (!(SOURCE_EDITS as readonly string[]).includes(k) || typeof v !== 'string' || v.length > 300) return `Unknown or too long: ${k}.`;
    }
    if (value.level !== undefined && !(SOURCE_LEVELS as readonly unknown[]).includes(value.level)) return `Unknown level: ${s(value.level)}`;
    for (const k of ['id', 'inst'] as const) if (value[k] !== undefined && value[k] !== '' && !SOURCE_ID.test(s(value[k]))) return `“${s(value[k])}” cannot be a ${k === 'id' ? 'source' : 'institution'}: letters, digits, spaces, dots and dashes.`;
    if (value.date !== undefined && value.date !== '' && !DATE.test(s(value.date))) return 'A date is 2026, 2026-10 or 2026-10-15.';
    return '';
  }
  for (const k of Object.keys(value)) if (k !== 's' && k !== 'edit') return `Unknown: ${k}.`;
  if (value.s !== undefined && value.s !== 'accepted' && value.s !== 'rejected') return 'A decision is accepted or rejected.';
  if (value.edit === undefined) return '';
  if (!isObj(value.edit)) return 'Edits are an object.';
  const allowed: readonly string[] = ref.startsWith('E') ? ELEMENT_EDITS : LINK_EDITS;
  for (const [k, v] of Object.entries(value.edit)) {
    if (!allowed.includes(k)) return `Cannot change ${k}.`;
    if (k === 'strength') {
      if (v !== 1 && v !== 2 && v !== 3) return 'A strength is 1, 2 or 3.';
    } else if (typeof v !== 'string' || v.length > TEXT_MAX) return `${k} is a text of at most ${TEXT_MAX} characters.`;
  }
  if (value.edit.kind !== undefined && !(READING_KINDS as readonly unknown[]).includes(value.edit.kind)) return `Unknown kind: ${s(value.edit.kind)}`;
  if (value.edit.type !== undefined && !(LINK_TYPES as readonly unknown[]).includes(value.edit.type)) return `Unknown kind of link: ${s(value.edit.type)}`;
  return '';
}

export type Status = 'pending' | Decision;

export interface ReviewedElement extends ProposedElement {
  s: Status;
  /** Added to the strategy already, as this id. */
  added?: string;
  /** Changed by a reviewer. */
  edited: boolean;
}
export interface ReviewedLink extends ProposedLink {
  s: Status;
  added?: string;
  edited: boolean;
}
export interface Reviewed {
  source: ProposedSource;
  elements: ReviewedElement[];
  links: ReviewedLink[];
}

/** A reading with people's decisions and edits: what the inbox shows and what is added. */
export function reviewed(info: Pick<ReadingInfo, 'id' | 'reading' | 'added'>, inbox: Record<string, unknown>): Reviewed {
  const r = info.reading;
  const decision = (ref: string): InboxDecision => {
    const v = inbox[inboxKey(info.id, ref)];
    return isObj(v) && !inboxEntryProblem(inboxKey(info.id, ref), v) ? (v as InboxDecision) : {};
  };
  const own = inbox[inboxKey(info.id)];
  const sourceEdit = isObj(own) && !inboxEntryProblem(inboxKey(info.id), own) ? (own as Partial<ProposedSource>) : {};
  const source = { ...(r?.source ?? readingOf({}).reading.source), ...sourceEdit };
  const elements = (r?.elements ?? []).map((e) => {
    const d = decision(e.ref);
    const edit = d.edit ?? {};
    const keep = Object.fromEntries(ELEMENT_EDITS.flatMap((k) => (edit[k] !== undefined ? [[k, edit[k]]] : [])));
    return { ...e, ...keep, s: info.added[e.ref] ? 'accepted' : (d.s ?? 'pending'), added: info.added[e.ref], edited: Object.keys(keep).length > 0 } as ReviewedElement;
  });
  const links = (r?.links ?? []).map((l) => {
    const d = decision(l.ref);
    const edit = d.edit ?? {};
    const keep = Object.fromEntries(LINK_EDITS.flatMap((k) => (edit[k] !== undefined ? [[k, edit[k]]] : [])));
    return { ...l, ...keep, s: info.added[l.ref] ? 'accepted' : (d.s ?? 'pending'), added: info.added[l.ref], edited: Object.keys(keep).length > 0 } as ReviewedLink;
  });
  return { source, elements, links };
}

/** Each pending proposal of a reading set to `s` (Accept all, Reject all): the changes to save. */
export function decideAll(info: Pick<ReadingInfo, 'id' | 'reading' | 'added'>, inbox: Record<string, unknown>, s: Decision, refs?: readonly string[]): StrategyChange[] {
  const r = reviewed(info, inbox);
  const want = refs && new Set(refs);
  return [...r.elements, ...r.links]
    .filter((x) => x.s === 'pending' && (!want || want.has(x.ref)))
    .map((x) => {
      const was = inbox[inboxKey(info.id, x.ref)];
      return { section: 'inbox' as const, key: inboxKey(info.id, x.ref), value: { ...(isObj(was) ? was : {}), s } };
    });
}

// ── adding the accepted ones to the strategy ────────────────────────────────

export interface Applied {
  /** The new content: the old with the accepted elements, links and the document's source. */
  content: Obj;
  /** Changes beside the content: the document's library labels, a new institution for the views. */
  changes: StrategyChange[];
  /** Every proposal added, now and before: ref → its id in the strategy. */
  added: Record<string, string>;
  counts: { elements: number; links: number; same: number };
  /** Why something accepted could not be added. */
  problems: string[];
}

/** The ids the content and people's own elements, links and link decisions use. */
function usedIds(content: Obj, state: StrategyState): Set<string> {
  const ids = new Set<string>();
  for (const k of ['external', 'projects', 'directions', 'relations', 'overlaps']) for (const x of list(content[k])) ids.add(s(x.id));
  for (const k of Object.keys(state.edits ?? {})) ids.add(k);
  for (const k of Object.keys(state.links ?? {})) ids.add(k);
  for (const k of Object.keys(state.decisions ?? {})) ids.add(k);
  return ids;
}

/** The institutions the views know: theirs, people's own, and the library's. */
const knownInstitutions = (content: Obj, state: StrategyState): Set<string> =>
  new Set([...VIEW_INSTITUTIONS, ...Object.keys(state.institutions ?? {}), ...libraryInstitutions(content, state).map((i) => i.id)]);

/** The element ids of the content and people's own elements. */
function elementIds(content: Obj, state: StrategyState): Set<string> {
  const ids = new Set<string>();
  for (const k of ['external', 'projects', 'directions']) for (const x of list(content[k])) ids.add(s(x.id));
  for (const [id, e] of Object.entries(state.edits ?? {})) if (isObj(e) && e.isNew && !e.deleted) ids.add(id);
  return ids;
}

/** The first part of the ids an institution's elements have ("MF" for VU MF's MF-2.1.3); else the institution itself. */
function prefixOf(content: Obj, inst: string): string {
  const n = new Map<string, number>();
  for (const x of list(content.external)) if (s(x.source) === inst) {
    const p = s(x.id).split('-')[0]!;
    n.set(p, (n.get(p) ?? 0) + 1);
  }
  const best = [...n].sort((a, b) => b[1] - a[1])[0]?.[0];
  return best || inst.replace(/[^A-Za-z0-9]+/g, '').toUpperCase() || 'X';
}

/** The next link number after the content's highest: R207. */
function nextLink(content: Obj, used: Set<string>): () => string {
  let n = Math.max(0, ...list(content.relations).map((r) => /^R(\d+)$/.exec(s(r.id))).map((m) => (m ? Number(m[1]) : 0)));
  return () => {
    let id: string;
    do id = `R${String(++n).padStart(3, '0')}`;
    while (used.has(id));
    used.add(id);
    return id;
  };
}

/**
 * The accepted proposals of a reading added to `content`: a new content, and the changes to save with it.
 *   elements   an institution's projects (a unit's, VPC-17) go into projects, everything else into external,
 *              under the institution, with the document's words and place. One the strategy already has
 *              (sameAs) adds nothing: its links go to that one
 *   links      both ends added, or in the strategy; Claude's (origin "claude") and proposed, so the views ask
 *              about them as about every Claude link. One the content has already (same ends, same kind) is left
 *   source     the document as a source of the content, when it is a new one
 *   changes    the document's library labels, when it has none; a new institution, when the views lack it
 * What was added before (info.added) stays: links to it work, and a parent added later is filled in.
 */
export function applyReading(content: Obj, state: StrategyState, info: Pick<ReadingInfo, 'id' | 'doc' | 'name' | 'reading' | 'added'>, inbox: Record<string, unknown>): Applied {
  const r = reviewed(info, inbox);
  const problems: string[] = [];
  const out: Obj = { ...content };
  const external = list(content.external).map((x) => ({ ...x }));
  const projects = list(content.projects).map((x) => ({ ...x }));
  const relations = list(content.relations).map((x) => ({ ...x }));
  const sources = list(content.sources).map((x) => ({ ...x }));
  const used = usedIds(content, state);
  const known = elementIds(content, state);
  const added: Record<string, string> = { ...info.added };
  const counts = { elements: 0, links: 0, same: 0 };

  const src = r.source;
  const inst = src.inst || src.id;
  if (!inst || !SOURCE_ID.test(inst)) return { content, changes: [], added, counts, problems: ['The document has no institution: choose one first.'] };
  const srcId = src.id || inst;
  const units = new Set(projects.map((p) => s(p.unit) || 'VPC'));
  const prefix = prefixOf(content, inst);

  // Ids first, so parents and links can name elements added in the same go.
  const fresh = r.elements.filter((e) => e.s === 'accepted' && !e.added);
  const idOf = new Map<string, string>(Object.entries(added));
  for (const e of fresh) {
    if (e.sameAs && known.has(e.sameAs)) {
      idOf.set(e.ref, e.sameAs);
      continue;
    }
    if (e.sameAs) problems.push(`${e.ref}: ${e.sameAs} is not in the strategy; added as a new element.`);
    let id: string;
    if (e.kind === 'project' && units.has(inst)) {
      let n = 1;
      while (used.has(`${inst}-${String(n).padStart(2, '0')}`)) n++;
      id = `${inst}-${String(n).padStart(2, '0')}`;
    } else if (e.code && !used.has(`${prefix}-${e.code}`)) id = `${prefix}-${e.code}`;
    else {
      const base = e.code ? `${prefix}-${e.code}-` : `${prefix}-N`;
      let n = e.code ? 2 : 1;
      while (used.has(`${base}${n}`)) n++;
      id = `${base}${n}`;
    }
    used.add(id);
    idOf.set(e.ref, id);
  }
  const resolve = (end: string) => (REF.test(end) ? idOf.get(end) : known.has(end) ? end : undefined);
  // A project's place: under a direction (dir), a programme (prog), else a goal (goal), as the content's projects.
  const kindOf = new Map<string, string>([
    ...list(content.directions).map((d) => [s(d.id), 'direction'] as [string, string]),
    ...external.map((x) => [s(x.id), s(x.kind)] as [string, string]),
    ...fresh.map((e) => [idOf.get(e.ref)!, e.kind] as [string, string]),
  ]);
  const placeOf = (parent: string) => (kindOf.get(parent) === 'direction' ? 'dir' : kindOf.get(parent) === 'programme' ? 'prog' : 'goal');
  for (const e of fresh) {
    const id = idOf.get(e.ref)!;
    if (e.sameAs && id === e.sameAs) {
      added[e.ref] = id;
      counts.same++;
      continue;
    }
    const parent = e.parent ? (resolve(e.parent) ?? '') : '';
    const en: Obj = { title: e.titleEn };
    if (e.kpiEn) en.kpi = e.kpiEn;
    if (e.targetEn) en.target = e.targetEn;
    if (e.actionsEn.length) en.actions = e.actionsEn;
    const from = { origin: 'claude', reading: info.id, doc: info.name };
    if (e.kind === 'project' && units.has(inst)) {
      projects.push({
        id,
        unit: inst,
        title: e.title,
        problem: '',
        evidence: [srcId, e.loc, e.quote],
        actions: e.actions,
        kpi: e.kpi,
        baseline: '',
        ...(e.target ? { target: e.target } : {}),
        ...(e.owner ? { owner: e.owner } : {}),
        ...(e.deadline ? { deadline: e.deadline } : {}),
        ...(parent ? { [placeOf(parent)]: parent } : {}),
        ...from,
        en,
      });
    } else {
      external.push({
        id,
        source: inst,
        kind: e.kind,
        code: e.code,
        ...(parent ? { parent } : {}),
        title: e.title,
        loc: e.loc,
        ...(e.quote ? { quote: e.quote } : {}),
        ...(e.owner ? { owner: e.owner } : {}),
        ...(e.deadline ? { deadline: e.deadline } : {}),
        ...(e.kpi ? { kpi: e.kpi } : {}),
        ...(e.target ? { target: e.target } : {}),
        ...from,
        en,
      });
    }
    known.add(id);
    added[e.ref] = id;
    counts.elements++;
  }
  // Elements added before whose parent was not: filled in now that it is.
  for (const e of r.elements) {
    // One the strategy had already (sameAs) is left as it is, as on its first add.
    if (!e.added || !e.parent || e.added === e.sameAs) continue;
    const parent = resolve(e.parent);
    if (!parent) continue;
    const x = external.find((y) => s(y.id) === e.added);
    const p = projects.find((y) => s(y.id) === e.added);
    if (x && !x.parent) x.parent = parent;
    if (p && !p.dir && !p.goal && !p.prog) p[placeOf(parent)] = parent;
  }

  const has = new Set(relations.map((x) => `${s(x.from_ ?? x.from)} ${s(x.type)} ${s(x.to)}`));
  const next = nextLink(content, used);
  for (const l of r.links) {
    if (l.s !== 'accepted' || l.added) continue;
    const from = resolve(l.from);
    const to = resolve(l.to);
    // An end not accepted (yet), or no longer in the strategy: the link waits, or goes with it.
    if (!from || !to || !known.has(from) || !known.has(to)) continue;
    if (has.has(`${from} ${l.type} ${to}`)) {
      // The content has it already: counted as added, under its own number.
      const there = relations.find((x) => s(x.from_ ?? x.from) === from && s(x.to) === to && s(x.type) === l.type);
      if (there) added[l.ref] = s(there.id);
      continue;
    }
    const id = next();
    relations.push({
      id,
      from_: from,
      to,
      type: l.type,
      strength: l.strength,
      rationale: l.why,
      origin: 'claude',
      status: 'proposed',
      ...(l.quote ? { quote: l.quote } : {}),
      reading: info.id,
      en: { rationale: l.whyEn },
    });
    has.add(`${from} ${l.type} ${to}`);
    added[l.ref] = id;
    counts.links++;
  }

  const changes: StrategyChange[] = [];
  if (counts.elements || counts.links || counts.same) {
    if (srcId && !sources.some((x) => s(x.id) === srcId)) {
      sources.push({ id: srcId, title: src.title || info.name, level: src.level, date: src.date, path: info.name, ...(src.titleEn ? { en: { title: src.titleEn } } : {}) });
    }
    // The document's labels: its institution and source, when nobody gave them.
    const { files, links } = libraryEntries(state);
    if (info.doc.startsWith('f:')) {
      const l: LibraryLabels = files.get(info.doc.slice(2)) ?? {};
      if (!l.src || !l.inst) changes.push({ section: 'library', key: info.doc, value: { ...l, inst: l.inst || inst, src: l.src || srcId } });
    } else if (info.doc.startsWith('l:')) {
      const link = links.find((x) => x.id === info.doc.slice(2));
      if (link && (!link.src || !link.inst)) {
        const { id: _id, ...rest } = link;
        changes.push({ section: 'library', key: info.doc, value: { ...rest, inst: link.inst || inst, src: link.src || srcId } satisfies LibraryLink });
      }
    }
    // An institution the views do not know: added as one of theirs, so its elements show.
    const insts = knownInstitutions(content, state);
    if (inst && !insts.has(inst) && !units.has(inst)) {
      if (NEW_INST_ID.test(inst)) {
        changes.push({
          section: 'institutions',
          key: inst,
          value: { id: inst, lt: src.instName || inst, en: src.instNameEn || src.instName || inst, level: src.level, pal: 6, doc: info.name },
        });
      } else problems.push(`The views will not show ${inst} until it is added there (“+ institution”, a code of capitals, digits and dashes).`);
    }
  }
  out.external = external;
  out.projects = projects;
  out.relations = relations;
  out.sources = sources;
  return { content: out, changes, added, counts, problems };
}

/** The institution a document's elements go to, as the library shows it: its source's owner, else the source. */
export const readingInstitution = (src: ProposedSource, content: Obj, state: StrategyState): string =>
  src.inst || (src.id ? institutionOfSource(src.id, libraryInstitutions(content, state)) : '');

/** The strategy's elements as Claude is told them: id, institution, kind, code, title (Lithuanian, then English). */
export function elementsForReading(content: Obj, state: StrategyState): { id: string; inst: string; kind: string; code: string; title: string; titleEn: string }[] {
  const en = (x: Obj, k: string) => (isObj(x.en) ? s(x.en[k]) : '');
  const out: { id: string; inst: string; kind: string; code: string; title: string; titleEn: string }[] = [];
  for (const d of list(content.directions)) out.push({ id: s(d.id), inst: s(d.unit) || 'VPC', kind: 'direction', code: s(d.id), title: s(d.title), titleEn: en(d, 'title') });
  for (const p of list(content.projects)) out.push({ id: s(p.id), inst: s(p.unit) || 'VPC', kind: 'project', code: s(p.id), title: s(p.title), titleEn: en(p, 'title') });
  for (const x of list(content.external)) out.push({ id: s(x.id), inst: s(x.source), kind: s(x.kind), code: s(x.code), title: s(x.title), titleEn: en(x, 'title') });
  for (const [id, e] of Object.entries(state.edits ?? {})) {
    if (!isObj(e) || !e.isNew || e.deleted) continue;
    const txt = isObj(e.txt) ? e.txt : {};
    const t = (l: string) => (isObj(txt[l]) ? s((txt[l] as Obj).title) : '');
    out.push({ id, inst: s(e.inst) || 'VPC', kind: 'element', code: '', title: t('lt') || t('en'), titleEn: t('en') });
  }
  const deleted = (id: string) => isObj(state.edits?.[id]) && !!(state.edits[id] as Obj).deleted;
  return out.filter((x) => x.id && !deleted(x.id));
}

/**
 * The strategy's elements a reading names and the strategy lacks: a parent, a sameAs, a link's end. Adding would
 * leave them out without a word (applyReading), so Claude Code fixes them before the reading is stored.
 */
export function readingIdProblems(reading: Reading, content: Obj, state: StrategyState): string[] {
  const known = elementIds(content, state);
  // The institution: one the views know, or a new code as they take it (applyReading adds it then).
  const inst = reading.source.inst;
  const instProblem = inst && !knownInstitutions(content, state).has(inst) && !NEW_INST_ID.test(inst) ? [`source.inst ${inst} is neither an institution the strategy lists nor a new code (capitals, digits, dashes; at most 12).`] : [];
  const titles = new Map(reading.elements.map((e) => [e.ref, `“${e.title.slice(0, 60)}”`]));
  const named = (end: string) => titles.get(end) ?? end;
  const ok = (end: string) => !end || titles.has(end) || known.has(end);
  const p: string[] = [];
  for (const e of reading.elements) {
    if (!ok(e.parent)) p.push(`${named(e.ref)}: parent ${e.parent} is neither an element of this reading nor of the strategy.`);
    if (e.sameAs && !known.has(e.sameAs)) p.push(`${named(e.ref)}: sameAs ${e.sameAs} is no element of the strategy.`);
  }
  for (const l of reading.links) for (const end of [l.from, l.to]) if (!ok(end)) p.push(`The link ${named(l.from)} → ${named(l.to)}: ${end} is no element of the strategy.`);
  return [...instProblem, ...p];
}

/** The content's sources and institutions, for Claude to say which the document is. */
export function sourcesForReading(content: Obj, state: StrategyState): { sources: { id: string; title: string; level: string }[]; institutions: string[] } {
  return {
    sources: sourcesOf(content).map((x) => ({ id: x.id, title: x.titleEn || x.title, level: x.level })),
    institutions: [...new Set([...VIEW_INSTITUTIONS, ...libraryInstitutions(content, state).map((i) => i.id)])],
  };
}
